import { useEffect, useRef, useState } from 'react';

import { createVisibilityGate } from './visibility-gate';

import type { VisibilityGate } from './visibility-gate';
import type { RefObject } from 'react';

/**
 * React binding for {@link createVisibilityGate} — a ref to attach to the
 * section, and a boolean that re-renders when the gate opens or closes.
 *
 * The gate module shipped without one, and that is a large part of why it fell
 * out of use: its only consumer was an async loop, which can hold the gate in a
 * closure and never needs a re-render. When that loop was deleted with the
 * superseded landing sections the gate became unreachable — while `ADR-0195`
 * went on describing "landing animation drivers pause offscreen via the shared
 * visibility-gate" as the site's perf model. Meanwhile the how-it-works band ran
 * a 6.5s step interval and a per-character typing simulator from mount, on every
 * page load, on every device, scrolled past or backgrounded, for the session.
 *
 * ## Why this subscribes rather than polls
 *
 * The obvious binding reads `gate.active` on scroll/resize. It is wrong, and it
 * fails in the most confusing possible direction. `IntersectionObserver`
 * delivers asynchronously, so a scroll-driven read sees the state from before
 * the scroll: measured on the landing page, the band froze while it sat in view
 * and ran once the reader had scrolled past it — the gate inverted. The gate now
 * takes an `onChange` callback and this hook is a thin subscriber to it.
 *
 * SSR and jsdom have no `IntersectionObserver`, so the gate opens permanently
 * there and drivers behave exactly as they did before it existed. The initial
 * state is therefore `true`: a driver must never be stalled by a gate that has
 * not attached yet, since starting a beat late is invisible while never starting
 * is a dead demo.
 */
export interface VisibilityGateBinding<T extends Element> {
  readonly ref: RefObject<T>;
  /** Section in view AND document visible. Re-renders on change. */
  readonly active: boolean;
  /** The gate itself, for async drivers that thread their sleeps through it. */
  readonly gate: RefObject<VisibilityGate | null>;
}

export function useVisibilityGate<T extends Element>(): VisibilityGateBinding<T> {
  const ref = useRef<T>(null);
  const gate = useRef<VisibilityGate | null>(null);
  const [active, setActive] = useState(true);

  useEffect(() => {
    const created = createVisibilityGate(ref.current, setActive);
    gate.current = created;
    // The observer has not reported yet, so `created.active` is its pessimistic
    // pre-observation value (false whenever a target is being observed). Seeding
    // React state from it would blank the driver for a frame on a section that
    // is already in view; the first `onChange` is what carries the truth.
    return () => {
      created.dispose();
      gate.current = null;
    };
  }, []);

  return { ref, active, gate };
}
